import OpenAI from 'openai'
import type {
  MetadataGenerator,
  Metadata,
  GenerationOptions,
  GenerationProgress,
  MetadataUpdate,
  ValidationResult,
  ValidationResultError,
  ValidationWarning,
  ImageAnalysis,
} from '../contracts/metadataGenerator'
import {
  GenerationError,
  ValidationError,
  GENERATION_CODES,
  STOCK_CATEGORIES,
  PLATFORM_CATEGORIES,
} from '../contracts/metadataGenerator'
import type { ImageFile } from '../contracts/uploadService'

interface AIVisionConfig {
  openaiApiKey: string
  model?: 'gpt-4o' | 'gpt-4o-mini'
  maxTokens?: number
  temperature?: number
}

export class AIVisionMetadataGenerator implements MetadataGenerator {
  private openai: OpenAI | null = null
  private batches = new Map<string, GenerationProgress>()
  private config: AIVisionConfig | null = null

  constructor(config?: AIVisionConfig) {
    if (config?.openaiApiKey) {
      this.setConfig(config)
    }
  }

  setConfig(config: AIVisionConfig) {
    this.config = config
    this.openai = new OpenAI({
      apiKey: config.openaiApiKey,
      dangerouslyAllowBrowser: true, // Note: In production, use a backend proxy
    })
  }

  async generateMetadata(image: ImageFile, options?: GenerationOptions): Promise<Metadata> {
    if (!this.openai || !this.config) {
      throw new GenerationError(
        'OpenAI API not configured. Please set API key.',
        GENERATION_CODES.IMAGE_ANALYSIS_FAILED,
        image.id,
        { error: 'Missing API configuration' }
      )
    }

    try {
      console.log('Generating AI metadata for image:', image.name)
      
      // Convert media to base64 (image or video frame)
      const isVideo = image.type.startsWith('video/')
      const base64Image = isVideo ? await this.extractVideoFrame(image) : await this.imageToBase64(image)
      
      // Generate analysis and metadata using GPT-4 Vision (includes style analysis)
      const aiResponse = await this.callVisionAPI(base64Image, options, isVideo)
      const analysis = this.parseAIResponse(aiResponse)
      
      // Format description for Shutterstock editorial if needed
      let formattedDescription = analysis.description
      if (options?.context?.platform === 'shutterstock' && !analysis.isCommercial) {
        formattedDescription = this.formatShutterstockEditorialDescription(
          analysis.description, 
          options.context
        )
      }

      // Debug logging for editorial vs commercial detection
      console.log('Editorial/Commercial analysis:', {
        isCommercial: analysis.isCommercial,
        title: analysis.title,
        description: formattedDescription.substring(0, 100),
        complianceFlags: analysis.complianceFlags,
        platform: options?.context?.platform
      })

      const metadata: Metadata = {
        imageId: image.id,
        title: analysis.title,
        description: formattedDescription,
        keywords: analysis.keywords.slice(0, this.getKeywordLimit(options?.context?.platform)),
        category: options?.category || this.selectBestCategory(analysis, options?.context),
        isCommercial: analysis.isCommercial,
        hasModelRelease: false, // User must manually verify
        hasPropertyRelease: false, // User must manually verify
        complianceFlags: analysis.complianceFlags,
        confidence: analysis.confidence,
        isEdited: false,
        generatedAt: new Date(),
        analysis: analysis.imageAnalysis, // Include AI analysis for better export decisions
      }

      if (options?.confidenceThreshold && metadata.confidence < options.confidenceThreshold) {
        throw new GenerationError(
          'Generated metadata confidence below threshold',
          GENERATION_CODES.INSUFFICIENT_QUALITY,
          image.id,
          { confidence: metadata.confidence, threshold: options.confidenceThreshold }
        )
      }

      console.log('AI metadata generated successfully:', metadata)
      return metadata

    } catch (error) {
      console.error('AI metadata generation error:', error)
      if (error instanceof GenerationError) {
        throw error
      }

      // Check for OpenAI API authentication errors (401)
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('401') || errorMessage.includes('Incorrect API key')) {
        console.error('❌ OpenAI API Key Authentication Failed')
        console.error('🔧 This usually means:')
        console.error('   1. The API key is invalid or has been revoked')
        console.error('   2. The API key was not properly set in Vercel environment variables')
        console.error('   3. The production build needs to be redeployed after updating env vars')
        console.error(`📋 Current error: ${errorMessage}`)

        throw new GenerationError(
          'OpenAI API authentication failed. Please check that VITE_OPENAI_API_KEY is correctly set in Vercel environment variables and redeploy.',
          GENERATION_CODES.IMAGE_ANALYSIS_FAILED,
          image.id,
          {
            originalError: errorMessage,
            errorType: 'authentication_error',
            suggestedFix: 'Update VITE_OPENAI_API_KEY in Vercel and trigger a new deployment'
          }
        )
      }

      throw new GenerationError(
        'Failed to generate AI metadata',
        GENERATION_CODES.IMAGE_ANALYSIS_FAILED,
        image.id,
        { originalError: errorMessage }
      )
    }
  }

  async batchGenerate(
    images: ImageFile[],
    options?: GenerationOptions
  ): Promise<Record<string, Metadata>> {
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    const results: Record<string, Metadata> = {}

    this.batches.set(batchId, {
      batchId,
      totalImages: images.length,
      processedImages: 0,
      status: 'initializing',
    })

    try {
      this.batches.set(batchId, {
        batchId,
        totalImages: images.length,
        processedImages: 0,
        status: 'processing',
      })

      for (let i = 0; i < images.length; i++) {
        const image = images[i]
        
        this.batches.set(batchId, {
          batchId,
          totalImages: images.length,
          processedImages: i,
          currentImageId: image.id,
          status: 'processing',
        })

        try {
          const metadata = await this.generateMetadata(image, options)
          results[image.id] = metadata
          
          // Add delay to respect API rate limits
          if (i < images.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        } catch (error) {
          console.error(`Failed to generate metadata for ${image.name}:`, error)
          // Continue with other images instead of failing entire batch
        }
      }

      this.batches.set(batchId, {
        batchId,
        totalImages: images.length,
        processedImages: images.length,
        status: 'completed',
      })

      return results
    } catch (error) {
      this.batches.set(batchId, {
        batchId,
        totalImages: images.length,
        processedImages: Object.keys(results).length,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  }

  async updateMetadata(imageId: string, update: MetadataUpdate): Promise<Metadata> {
    // For now, return updated metadata (in real app, this would update storage)
    const existingMetadata: Metadata = {
      imageId,
      title: 'Original AI Title',
      description: 'Original AI description',
      keywords: ['original', 'ai', 'keywords'],
      category: 'Technology',
      isCommercial: true,
      hasModelRelease: false,
      hasPropertyRelease: false,
      complianceFlags: [],
      confidence: 0.85,
      isEdited: false,
      generatedAt: new Date(Date.now() - 3600000),
    }

    const updatedMetadata: Metadata = {
      ...existingMetadata,
      ...update,
      isEdited: true,
      editedAt: new Date(),
    }

    const validation = this.validateMetadata(updatedMetadata)
    if (!validation.isValid) {
      throw new ValidationError(
        'Updated metadata validation failed',
        'metadata',
        updatedMetadata
      )
    }

    return updatedMetadata
  }

  getGenerationProgress(batchId: string): GenerationProgress {
    return this.batches.get(batchId) || {
      batchId,
      totalImages: 0,
      processedImages: 0,
      status: 'initializing',
    }
  }

  async cancelGeneration(batchId: string): Promise<void> {
    this.batches.delete(batchId)
  }

  validateMetadata(metadata: Metadata, platform?: string): ValidationResult {
    const errors: ValidationResultError[] = []
    const warnings: ValidationWarning[] = []

    // Validate title
    if (!metadata.title || metadata.title.length < 3) {
      errors.push({
        field: 'title',
        message: 'Title must be at least 3 characters long',
        code: 'TITLE_TOO_SHORT',
      })
    } else if (metadata.title.length > 200) {
      errors.push({
        field: 'title',
        message: 'Title cannot exceed 200 characters',
        code: 'TITLE_TOO_LONG',
      })
    }

    // Validate description
    if (!metadata.description || metadata.description.length < 10) {
      errors.push({
        field: 'description',
        message: 'Description must be at least 10 characters long',
        code: 'DESCRIPTION_TOO_SHORT',
      })
    } else if (metadata.description.length > 1000) {
      errors.push({
        field: 'description',
        message: 'Description cannot exceed 1000 characters',
        code: 'DESCRIPTION_TOO_LONG',
      })
    }

    // Validate keywords
    if (!metadata.keywords || metadata.keywords.length === 0) {
      errors.push({
        field: 'keywords',
        message: 'At least one keyword is required',
        code: 'NO_KEYWORDS',
      })
    } else if (metadata.keywords.length > 50) {
      errors.push({
        field: 'keywords',
        message: 'Cannot exceed 50 keywords',
        code: 'TOO_MANY_KEYWORDS',
      })
    } else if (metadata.keywords.length < 5) {
      warnings.push({
        field: 'keywords',
        message: 'Consider adding more keywords (recommended: 10-15)',
        suggestion: 'Add more relevant, specific keywords to improve discoverability',
      })
    }

    // Validate category using platform-specific categories
    const validCategories = platform && platform in PLATFORM_CATEGORIES
      ? PLATFORM_CATEGORIES[platform as keyof typeof PLATFORM_CATEGORIES]
      : PLATFORM_CATEGORIES['generic']
    
    if (!(validCategories as readonly string[]).includes(metadata.category)) {
      errors.push({
        field: 'category',
        message: `Invalid category for ${platform || 'generic'} platform. Must be one of: ${validCategories.join(', ')}`,
        code: 'INVALID_CATEGORY',
      })
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  async analyzeImage(image: ImageFile): Promise<ImageAnalysis> {
    if (!this.openai) {
      throw new Error('OpenAI not configured')
    }

    try {
      const base64Image = await this.imageToBase64(image)
      return await this.analyzeImageFromBase64(base64Image)
    } catch (error) {
      console.error('AI analysis error:', error)
      throw error
    }
  }

  getAvailableCategories(platform?: string): string[] {
    if (platform && platform in PLATFORM_CATEGORIES) {
      return [...PLATFORM_CATEGORIES[platform as keyof typeof PLATFORM_CATEGORIES]]
    }
    return [...PLATFORM_CATEGORIES['generic']]
  }

  getSuggestedKeywords(category?: string): string[] {
    // Return some basic suggestions - in real implementation, could be AI-powered
    const suggestions = [
      'professional', 'high-quality', 'stock', 'commercial', 'business',
      'creative', 'modern', 'digital', 'abstract', 'colorful'
    ]
    return suggestions
  }

  async regenerateMetadata(imageId: string, options?: GenerationOptions): Promise<Metadata> {
    // In real implementation, would retrieve original image and regenerate
    throw new Error('Regenerate requires original image file - not implemented in demo')
  }

  private async imageToBase64(image: ImageFile): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        
        // Check if image is large and needs compression
        const fileSizeMB = image.file.size / (1024 * 1024)
        console.log(`Image size: ${fileSizeMB.toFixed(2)}MB`)
        
        if (fileSizeMB > 5) {
          console.log('Large image detected, compressing for AI analysis...')
          this.compressImageForAI(result, image.name).then(resolve).catch(reject)
        } else {
          console.log('Image size acceptable, sending without compression')
          resolve(result)
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(image.file)
    })
  }

  private async compressImageForAI(base64Image: string, imageName?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      const img = new Image()
      img.onload = () => {
        // Calculate optimal dimensions for AI analysis (max 1024px on longest side)
        const maxDimension = 1024
        let { width, height } = img
        const originalDimensions = `${width}x${height}`
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width
            width = maxDimension
          } else {
            width = (width * maxDimension) / height  
            height = maxDimension
          }
        }

        canvas.width = Math.round(width)
        canvas.height = Math.round(height)
        
        // Use better image interpolation for quality
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, Math.round(width), Math.round(height))
        
        // Use 85% quality for good balance of size vs detail
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.85)
        
        // Calculate compression statistics
        const originalSize = base64Image.length
        const compressedSize = compressedBase64.length
        const compressionRatio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1)
        const newDimensions = `${Math.round(width)}x${Math.round(height)}`
        
        console.log(`Image compression complete${imageName ? ` for ${imageName}` : ''}:`)
        console.log(`  Dimensions: ${originalDimensions} → ${newDimensions}`)
        console.log(`  Size reduction: ${compressionRatio}%`)
        console.log(`  Quality: 85% JPEG`)
        
        resolve(compressedBase64)
      }
      
      img.onerror = () => reject(new Error('Failed to load image for compression'))
      img.src = base64Image
    })
  }

  private async extractVideoFrame(image: ImageFile): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      video.preload = 'metadata'
      video.muted = true
      video.crossOrigin = 'anonymous'
      
      video.onloadedmetadata = () => {
        // Extract frame from 20% into the video (after intro/title cards)
        // This is more likely to contain actual content
        const seekTime = Math.max(0.5, video.duration * 0.2)
        video.currentTime = seekTime
      }

      video.onseeked = async () => {
        try {
          // Set canvas dimensions to video dimensions (up to max size for API efficiency)
          const maxDimension = 1024
          let { videoWidth: width, videoHeight: height } = video

          // Scale down if too large (for API efficiency)
          if (width > maxDimension || height > maxDimension) {
            const scale = Math.min(maxDimension / width, maxDimension / height)
            width = Math.round(width * scale)
            height = Math.round(height * scale)
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(video, 0, 0, width, height)

          // Check if image is large and needs compression (same logic as for images)
          const base64 = canvas.toDataURL('image/jpeg', 0.9)
          const fileSizeMB = (base64.length * 3) / 4 / (1024 * 1024) // Rough base64 to bytes conversion
          
          if (fileSizeMB > 5) {
            console.log(`Video frame is ${fileSizeMB.toFixed(2)}MB, compressing for AI...`)
            const compressedBase64 = await this.compressImageForAI(base64, image.name)
            resolve(compressedBase64)
          } else {
            resolve(base64)
          }
        } catch (error) {
          console.error('Error extracting video frame:', error)
          reject(new Error('Failed to extract frame from video'))
        }
      }

      video.onerror = (e) => {
        console.error('Video loading error:', e)
        reject(new Error('Failed to load video for frame extraction'))
      }
      
      // Create object URL for the video file
      const url = URL.createObjectURL(image.file)
      video.src = url
      
      // Clean up object URL after processing
      video.addEventListener('loadedmetadata', () => {
        setTimeout(() => URL.revokeObjectURL(url), 5000)
      })
    })
  }

  private async callVisionAPI(base64Image: string, options?: GenerationOptions, isVideo: boolean = false): Promise<string> {
    if (!this.openai) {
      throw new Error('OpenAI not configured')
    }

    const style = options?.style || 'descriptive'
    const keywordLimit = this.getKeywordLimit(options?.context?.platform)
    const context = options?.context

    // Get platform-specific categories
    const platformCategories = context?.platform 
      ? PLATFORM_CATEGORIES[context.platform]
      : PLATFORM_CATEGORIES['generic']

    // Build context information
    let contextInfo = ''
    if (context && (context.location || context.title || context.month || context.year || context.platform)) {
      contextInfo = '\n\nADDITIONAL CONTEXT INFORMATION:'
      if (context.location) {
        contextInfo += `\n- Location: ${context.location}`
      }
      if (context.title) {
        contextInfo += `\n- Event/Theme: ${context.title}`
      }
      if (context.date) {
        const date = new Date(context.date)
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December']
        const monthName = monthNames[date.getMonth()]
        const day = date.getDate()
        const year = date.getFullYear()
        contextInfo += `\n- Date: ${monthName} ${day}, ${year}`
      } else if (context.month && context.year) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December']
        contextInfo += `\n- Date: ${monthNames[context.month - 1]} ${context.year}`
      } else if (context.month) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December']
        contextInfo += `\n- Month: ${monthNames[context.month - 1]}`
      } else if (context.year) {
        contextInfo += `\n- Year: ${context.year}`
      }
      if (context.platform) {
        const platformName = {
          'shutterstock': 'Shutterstock (includes 2025 trending: Remote Work, Sustainability, Diversity & Inclusion, Wellness)',
          'adobe-stock': 'Adobe Stock',
          'getty-images': 'Getty Images',
          'istock': 'iStock',
          'generic': 'Generic/Multiple Platforms'
        }[context.platform]
        contextInfo += `\n- Target Platform: ${platformName}`
        contextInfo += `\n- Available Categories: ${platformCategories.join(', ')}`
      }
      
      contextInfo += '\n\nIMPORTANT: Use this context information to:'
      contextInfo += '\n- For EDITORIAL images: Include properly formatted location and date info in title/description as required by stock platforms (e.g., "City, Country - Month DD, YYYY: Description")'
      contextInfo += '\n- For COMMERCIAL images: Incorporate location and thematic keywords naturally into title, description, and keywords'
      contextInfo += '\n- Add seasonal, location-specific, and event-related keywords where relevant'
      if (context.platform) {
        contextInfo += `\n- CATEGORY SELECTION: Choose category ONLY from the ${context.platform} list provided above - do not use categories from other platforms`
        contextInfo += '\n- PLATFORM OPTIMIZATION: Tailor metadata style and keywords for the selected platform\'s audience and requirements'
      }
      contextInfo += '\n- Ensure all metadata reflects the provided context accurately'
    }

    const mediaType = isVideo ? 'video frame (from stock footage)' : 'image'
    const prompt = `Analyze this ${mediaType} for stock ${isVideo ? 'footage' : 'photography'} metadata. Create professional ${style} content.${contextInfo}

Return a JSON object with:
- title: Professional, SEO-friendly title (max ${this.getTitleLimit(context?.platform)} chars) ${context?.location || context?.title || context?.date || (context?.month && context?.year) ? '- incorporate context information appropriately' : ''}
- description: Detailed description for stock photography (max ${this.getDescriptionLimit(context?.platform)} chars) ${context ? '- use context to add location/date/theme details' : ''}${context?.platform === 'shutterstock' && !context?.isCommercial ? ' - For editorial content, format as "City, State/Country - Month Day Year: Description"' : ''}
- keywords: Array of ${keywordLimit} relevant keywords for stock photography ${context ? '- include location, seasonal, and thematic keywords from context' : ''}
- isCommercial: true/false (false if contains people without model releases, recognizable locations, current events, news-worthy content, or copyrighted material)
- complianceFlags: Array of flags like "MODEL_RELEASE_REQUIRED", "PROPERTY_RELEASE_REQUIRED", "EDITORIAL_USE_ONLY"
- confidence: Float 0.0-1.0 representing how confident you are in this analysis
- imageAnalysis: Object with {dominantColors: array of 3-5 hex colors, subjects: array of main subjects, composition: "landscape"/"portrait"/"square", brightness: "low"/"medium"/"high", complexity: "simple"/"moderate"/"complex", style: "photography"/"illustration"/"graphic"}

Focus on:
- Commercial stock photography appeal
- SEO optimization with context-specific keywords
- Marketplace compliance
- Professional language
- Location and date accuracy when provided
- Editorial formatting standards for news/documentary content

Editorial vs Commercial Guidelines:
- EDITORIAL (isCommercial: false): Real people without releases, identifiable locations, specific events, news content, public figures, protests, demonstrations
- COMMERCIAL (isCommercial: true): Generic subjects, unrecognizable locations, lifestyle concepts, business scenarios with releases
- When in doubt about people/locations, default to EDITORIAL for safety

Return only valid JSON, no other text.`

    const response = await this.openai.chat.completions.create({
      model: this.config?.model || 'gpt-4o',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: prompt,
            },
            {
              type: 'image_url',
              image_url: {
                url: base64Image,
              },
            },
          ],
        },
      ],
      max_tokens: this.config?.maxTokens || 1000,
      temperature: this.config?.temperature || 0.3,
    })

    const content = response.choices[0]?.message?.content
    if (!content) {
      throw new Error('No response from OpenAI Vision API')
    }

    return content
  }

  private parseAIResponse(response: string): any {
    try {
      // Remove markdown code block markers if present
      let cleanResponse = response.trim()
      if (cleanResponse.startsWith('```json')) {
        cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '')
      } else if (cleanResponse.startsWith('```')) {
        cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '')
      }
      
      const parsed = JSON.parse(cleanResponse)
      
      // Ensure required fields exist with defaults
      return {
        title: parsed.title || 'AI Generated Title',
        description: parsed.description || 'AI generated description',
        keywords: Array.isArray(parsed.keywords) ? parsed.keywords : [],
        isCommercial: parsed.isCommercial ?? true,
        complianceFlags: Array.isArray(parsed.complianceFlags) ? parsed.complianceFlags : [],
        confidence: typeof parsed.confidence === 'number' ? parsed.confidence : 0.8,
        imageAnalysis: parsed.imageAnalysis || {
          dominantColors: [],
          subjects: [],
          composition: 'landscape',
          brightness: 'medium',
          complexity: 'moderate',
          style: 'photography'
        }
      }
    } catch (error) {
      console.error('Failed to parse AI response:', response)
      throw new Error('Invalid AI response format')
    }
  }

  private selectBestCategory(analysis: any, context?: ImageContext): string {
    const platform = context?.platform
    
    // Get platform-specific categories or use generic
    const availableCategories = platform && platform in PLATFORM_CATEGORIES
      ? PLATFORM_CATEGORIES[platform as keyof typeof PLATFORM_CATEGORIES]
      : PLATFORM_CATEGORIES['generic']

    // Combine all available text for comprehensive analysis
    const allText = [
      ...(analysis.keywords || []),
      analysis.title || '',
      analysis.description || '',
      context?.title || '',
      context?.location || '',
    ].join(' ').toLowerCase()

    // Enhanced category mappings with scoring system
    const categoryMappings = [
      // Specific landmark/travel locations (medium priority, more specific)
      { 
        keywords: ['eiffel tower', 'statue of liberty', 'big ben', 'colosseum', 'landmark', 'monument', 'famous building'], 
        categories: ['Travel', 'Buildings/Landmarks'],
        weight: 3
      },
      { 
        keywords: ['beach', 'ocean', 'mountain', 'forest', 'national park', 'outdoor', 'landscape', 'scenic'], 
        categories: ['Nature', 'Landscapes', 'The Environment', 'Parks/Outdoor'],
        weight: 2
      },
      
      // Event/Theme-based (high priority for context)
      { 
        keywords: ['conference', 'meeting', 'presentation', 'corporate', 'business meeting'], 
        categories: ['Business/Finance', 'Education'],
        weight: 3
      },
      { 
        keywords: ['festival', 'celebration', 'party', 'event', 'concert', 'wedding'], 
        categories: ['Holidays', 'Arts Culture and Entertainment', 'Hobbies and Leisure'],
        weight: 2
      },
      
      // Subject-based (medium priority)
      { 
        keywords: ['person', 'people', 'human', 'portrait', 'face', 'individual', 'man', 'woman', 'child'], 
        categories: ['People'],
        weight: 3
      },
      { 
        keywords: ['technology', 'tech', 'computer', 'digital', 'software', 'device', 'ai'], 
        categories: ['Technology'],
        weight: 2
      },
      { 
        keywords: ['food', 'meal', 'cooking', 'cuisine', 'restaurant', 'chef', 'kitchen'], 
        categories: ['Food and Drink', 'Food'],
        weight: 2
      },
      { 
        keywords: ['drink', 'beverage', 'cocktail', 'wine', 'coffee', 'tea', 'bar'], 
        categories: ['Food and Drink', 'Drinks'],
        weight: 2
      },
      { 
        keywords: ['animal', 'pet', 'dog', 'cat', 'wildlife', 'mammal', 'bird'], 
        categories: ['Animals/Wildlife', 'Animals'],
        weight: 2
      },
      { 
        keywords: ['architecture', 'architectural', 'skyscraper', 'cathedral', 'castle', 'bridge'], 
        categories: ['Buildings and Architecture', 'Buildings/Landmarks'],
        weight: 2
      },
      { 
        keywords: ['house', 'home', 'residential', 'apartment', 'building exterior'], 
        categories: ['Buildings and Architecture'],
        weight: 1
      },
      { 
        keywords: ['car', 'vehicle', 'transport', 'plane', 'train', 'bus', 'traffic'], 
        categories: ['Transportation', 'Transport'],
        weight: 2
      },
      { 
        keywords: ['sport', 'game', 'athletic', 'fitness', 'exercise', 'gym'], 
        categories: ['Sports/Recreation', 'Sports', 'Hobbies and Leisure'],
        weight: 2
      },
      { 
        keywords: ['medical', 'health', 'doctor', 'hospital', 'treatment', 'surgery'], 
        categories: ['Healthcare/Medical'],
        weight: 2
      },
      { 
        keywords: ['education', 'school', 'learning', 'study', 'academic', 'classroom'], 
        categories: ['Education'],
        weight: 2
      },
      { 
        keywords: ['art', 'artistic', 'creative', 'painting', 'culture', 'museum'], 
        categories: ['Arts', 'Arts Culture and Entertainment'],
        weight: 2
      },
      { 
        keywords: ['science', 'research', 'laboratory', 'experiment', 'discovery'], 
        categories: ['Science'],
        weight: 2
      },
      { 
        keywords: ['travel', 'vacation', 'tourism', 'trip', 'destination', 'suitcase'], 
        categories: ['Travel'],
        weight: 2
      },
      { 
        keywords: ['interior', 'room', 'home', 'furniture', 'decoration', 'living', 'indoor'], 
        categories: ['Interiors', 'Lifestyle'],
        weight: 3
      },
      { 
        keywords: ['business', 'office', 'work', 'professional', 'corporate', 'workplace'], 
        categories: ['Business/Finance'],
        weight: 3
      },
      { 
        keywords: ['fashion', 'style', 'clothing', 'model', 'beauty', 'makeup'], 
        categories: ['Beauty/Fashion'],
        weight: 3
      },
      { 
        keywords: ['lifestyle', 'daily life', 'routine', 'casual', 'relaxed'], 
        categories: ['Lifestyle'],
        weight: 2
      },
      
      // Generic/fallback (lower priority)
      { 
        keywords: ['object', 'item', 'product', 'tool', 'equipment'], 
        categories: ['Objects'],
        weight: 2
      },
      { 
        keywords: ['background', 'texture', 'pattern', 'abstract'], 
        categories: ['Backgrounds/Textures'],
        weight: 1
      },
      { 
        keywords: ['vintage', 'retro', 'classic', 'old', 'antique'], 
        categories: ['Vintage'],
        weight: 1
      },
    ]

    // Score categories based on keyword matches and weights
    const categoryScores = new Map<string, number>()
    
    for (const mapping of categoryMappings) {
      const matchCount = mapping.keywords.filter(keyword => allText.includes(keyword)).length
      if (matchCount > 0) {
        for (const category of mapping.categories) {
          if ((availableCategories as readonly string[]).includes(category)) {
            const score = matchCount * mapping.weight
            categoryScores.set(category, (categoryScores.get(category) || 0) + score)
          }
        }
      }
    }

    // Return the highest scoring category
    if (categoryScores.size > 0) {
      const sortedCategories = Array.from(categoryScores.entries())
        .sort(([,a], [,b]) => b - a)
      
      // Debug logging to help diagnose categorization
      console.log('Category selection for text:', allText.substring(0, 100))
      console.log('Top categories:', sortedCategories.slice(0, 3))
      
      const bestCategory = sortedCategories[0][0]
      return bestCategory
    }

    // Return the first available category as fallback
    return availableCategories[0] || 'Miscellaneous'
  }

  private formatShutterstockEditorialDescription(description: string, context?: ImageContext): string {
    // If already in correct format, return as-is
    if (description.match(/^[^-]+ - \w+ \d{1,2},? \d{4}:/)) {
      return description
    }

    let formattedPrefix = ''
    
    // Build location part
    if (context?.location) {
      formattedPrefix = context.location
    }
    
    // Build date part - prioritize new date field over legacy month/year
    if (context?.date) {
      const date = new Date(context.date)
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const monthName = monthNames[date.getMonth()]
      const day = date.getDate()
      const year = date.getFullYear()
      const dateStr = `${monthName} ${day}, ${year}`
      
      if (formattedPrefix) {
        formattedPrefix += ` - ${dateStr}`
      } else {
        formattedPrefix = dateStr
      }
    } else if (context?.month && context?.year) {
      // Fallback to legacy month/year for backward compatibility
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const monthName = monthNames[context.month - 1]
      const dateStr = `${monthName} ${context.year}`
      
      if (formattedPrefix) {
        formattedPrefix += ` - ${dateStr}`
      } else {
        formattedPrefix = dateStr
      }
    } else if (context?.year) {
      if (formattedPrefix) {
        formattedPrefix += ` - ${context.year}`
      } else {
        formattedPrefix = context.year.toString()
      }
    }
    
    // If we have context, format as required
    if (formattedPrefix) {
      // Clean the description to remove any existing location/date prefixes
      let cleanDescription = description.replace(/^[^:]*:\s*/, '')
      return `${formattedPrefix}: ${cleanDescription}`
    }
    
    // If no context available, return original description
    return description
  }

  private getKeywordLimit(platform?: string): number {
    switch (platform) {
      case 'shutterstock': return 50
      case 'adobe-stock': return 49
      case 'getty-images':
      case 'istock': return 50
      case 'generic':
      default: return 50
    }
  }

  private getTitleLimit(platform?: string): number {
    switch (platform) {
      case 'adobe-stock': return 200
      case 'shutterstock': return 100
      default: return 100
    }
  }

  private getDescriptionLimit(platform?: string): number {
    switch (platform) {
      case 'shutterstock': return 200
      case 'adobe-stock': return 2000
      default: return 500
    }
  }
}